import React from 'react';
import { StyleSheet, View, Text, Image } from 'react-native';

interface SvgTemplateOverlayProps {
    svgTemplateUrl?: string;
    targetText?: string;
}

/**
 * Renders a faint tracing guide behind the drawing canvas.
 *
 * - If `svgTemplateUrl` is provided: loads the URL as an image (works with
 *   both SVG and raster URLs via react-native-web's <img> on web, and
 *   React Native's Image component on native).
 * - If only `targetText` is provided: falls back to a large ghost letter.
 * - Pointer events are disabled so the overlay never intercepts drawing gestures.
 */
export const SvgTemplateOverlay: React.FC<SvgTemplateOverlayProps> = ({
    svgTemplateUrl,
    targetText,
}) => {
    if (svgTemplateUrl) {
        return (
            <View style={styles.overlay} pointerEvents="none">
                <Image
                    testID="svg-template-image"
                    source={{ uri: svgTemplateUrl }}
                    style={styles.image}
                    resizeMode="contain"
                    accessibilityLabel={`Tracing template for ${targetText ?? 'letter'}`}
                    accessibilityRole="image"
                />
            </View>
        );
    }

    return (
        <View style={styles.overlay} pointerEvents="none">
            <Text
                testID="svg-template-fallback"
                style={styles.ghostText}
                accessibilityLabel={`Ghost letter ${targetText ?? ''}`}
                allowFontScaling={true}
                maxFontSizeMultiplier={1.5}
            >
                {targetText ?? ''}
            </Text>
        </View>
    );
};

const styles = StyleSheet.create({
    overlay: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'center',
        alignItems: 'center',
        opacity: 0.15,
    },
    image: {
        width: '100%',
        height: '100%',
    },
    ghostText: {
        fontSize: 300,
        fontWeight: '100',
        color: '#000',
    },
});
